import { propOr } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { useState, useEffect } from 'react';
import { fetchQuery, useRelayEnvironment } from 'react-relay';
import * as pdfjs from 'pdfjs-dist';
import PDFJSWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
import { QFileQuery } from '../_graphql/queries/documents/QFile';
import { base64toBlob } from '../pages/helpers/base64ToBlob';
import { getFileName } from '../utils/getFilename';
import { GAP_PAGE, MAX_VIEWPORT_SCALE } from '../_CONST';

const useFrameSynch = ({ fileId, frameRef, otherFrameRef, showDiff }) => {
  const [fileData, setFileData] = useState({});
  const [fileDataReturned, setFileDataReturned] = useState({});
  const [iframeHeight, setIframeHeight] = useState(50000);
  const [sourceElement, setSourceElement] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const environment = useRelayEnvironment();

  useEffect(() => {
    const fetchPdf = async () => {
      const { getFile } = await fetchQuery(environment, QFileQuery, { fileId }).toPromise();
      setFileData(getFile);
    };

    if (fileId) fetchPdf();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (frameRef) setSourceElement(frameRef.current);
    if (otherFrameRef) setTargetElement(otherFrameRef.current);
  }, [frameRef, otherFrameRef, showDiff]);

  useEffect(() => {
    const base64PdfString = propOr('', 'fileBase64', fileData);
    const fileName = getFileName(propOr('', 'fileName', fileData));
    const IMAGE_SRC = `data:image/png;base64,${base64PdfString}`;
    const pdfBlob = base64toBlob(base64PdfString, 'application/pdf');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    setFileDataReturned({
      fileName,
      IMAGE_SRC,
      pdfUrl
    });
    const setIframeHeightFromPageCount = async (pdfUrl) => {
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJSWorker;
      try {
        const pdfDocument = await pdfjs.getDocument({ url: pdfUrl }).promise;
        const numPages = pdfDocument.numPages;

        const page = await pdfDocument.getPage(1);
        const viewport = page.getViewport({ scale: MAX_VIEWPORT_SCALE });

        const iframeHeight = numPages * (viewport.height + GAP_PAGE);
        setIframeHeight(iframeHeight);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('pdfjs', error.message);
      }
    };

    setIframeHeightFromPageCount(pdfUrl);
  }, [fileData]);

  const synchronizeScrolling = (sourceElement, targetElement) => {
    if (isNotNilOrEmpty(sourceElement) && isNotNilOrEmpty(targetElement)) {
      const scrollY = sourceElement.scrollTop;
      targetElement.scrollTop = scrollY;
    }
  };

  return {
    iframeHeight,
    sourceElement,
    targetElement,
    fileDataReturned,
    synchronizeScrolling
  };
};

export default useFrameSynch;
